import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Trash2, AlertTriangle, Database, Building, Loader2, CheckCircle, 
  Users, UserX, Search, RefreshCw, User, Shield, Phone, Mail, Briefcase,
  BadgeX, Award, Link2, Unlink, FileWarning, Receipt, FileText, Scan, Terminal, Play, Copy
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const AdminDataManagement = () => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState('all');
  
  // Employee state
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  
  // User state
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  
  // Badge management state
  const [employeesWithBadges, setEmployeesWithBadges] = useState([]);
  const [selectedBadgeEmployees, setSelectedBadgeEmployees] = useState([]);
  const [badgeSearch, setBadgeSearch] = useState('');
  const [showClearBadgesDialog, setShowClearBadgesDialog] = useState(false);
  const [clearBadgesConfirmText, setClearBadgesConfirmText] = useState('');
  
  // Orphan data state
  const [orphanData, setOrphanData] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanError, setOrphanError] = useState(null);
  const [processingOrphan, setProcessingOrphan] = useState(null);
  const [diagnosisData, setDiagnosisData] = useState(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  
  // Delete office state
  const [selectedDeleteOffice, setSelectedDeleteOffice] = useState(null);
  const [officeDeletePreview, setOfficeDeletePreview] = useState(null);
  const [officeDeleteLoading, setOfficeDeleteLoading] = useState(false);
  const [showDeleteOfficeDialog, setShowDeleteOfficeDialog] = useState(false);
  const [deleteOfficeConfirmText, setDeleteOfficeConfirmText] = useState('');
  
  // Office-Employee Mismatch state
  const [mismatchData, setMismatchData] = useState(null);
  const [mismatchLoading, setMismatchLoading] = useState(false);
  
  // Office search state
  const [officeSearchName, setOfficeSearchName] = useState('');
  const [officeSearchResult, setOfficeSearchResult] = useState(null);
  const [officeSearchLoading, setOfficeSearchLoading] = useState(false);
  
  // Diagnostic Console state
  const [showDiagnosticConsole, setShowDiagnosticConsole] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticLogs, setDiagnosticLogs] = useState([]);
  const [testOfficeId, setTestOfficeId] = useState('');
  
  // Dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // 'employees' or 'users'
  const [confirmText, setConfirmText] = useState('');
  
  // Result/Error state
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const { getAuthHeaders, user, isSuperAdmin, token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const retryCountRef = React.useRef(0);

  useEffect(() => {
    // Only fetch data when user is loaded, is a super admin, and has a valid token
    if (user && token && isSuperAdmin()) {
      retryCountRef.current = 0;
      fetchData();
    }
  }, [selectedOffice, user, token]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    // Double check we have authentication
    if (!token) {
      setError('Authentication required. Please log in again.');
      setLoading(false);
      return;
    }
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Use AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      // Fetch offices first
      let officesData = [];
      try {
        const officesRes = await fetch(`${API_URL}/api/offices/`, { 
          headers,
          signal: controller.signal 
        });
        if (officesRes.ok) {
          officesData = await officesRes.json();
          setOffices(officesData);
        } else {
          console.error('Failed to fetch offices:', officesRes.status);
          if (officesRes.status === 401) {
            setError('Session expired. Please log in again.');
            setLoading(false);
            clearTimeout(timeoutId);
            return;
          }
        }
      } catch (officeErr) {
        if (officeErr.name === 'AbortError') {
          throw new Error('Request timeout - server took too long to respond');
        }
        console.error('Office fetch error:', officeErr);
      }
      
      // Fetch employees - for Super Admin with "all" selected, fetch from all offices
      let employeesData = [];
      try {
        const employeeHeaders = { ...headers };
        
        // If a specific office is selected, add the header
        if (selectedOffice && selectedOffice !== 'all') {
          employeeHeaders['X-Office-ID'] = selectedOffice;
          const employeesRes = await fetch(`${API_URL}/api/employees/`, { 
            headers: employeeHeaders,
            signal: controller.signal
          });
          if (employeesRes.ok) {
            employeesData = await employeesRes.json();
          } else {
            console.error('Failed to fetch employees:', employeesRes.status);
          }
        } else {
          // For "all offices", fetch employees from each office and combine
          // Or use the super admin endpoint if available
          const allEmployees = [];
          
          // Try fetching without X-Office-ID first (super admin may have access to all)
          const employeesRes = await fetch(`${API_URL}/api/employees/`, { 
            headers,
            signal: controller.signal
          });
          if (employeesRes.ok) {
            const data = await employeesRes.json();
            if (Array.isArray(data) && data.length > 0) {
              employeesData = data;
            }
          }
          
          // If that didn't work, fetch from each office
          if (employeesData.length === 0 && officesData.length > 0) {
            for (const office of officesData) {
              try {
                const officeHeaders = { ...headers, 'X-Office-ID': office.id };
                const res = await fetch(`${API_URL}/api/employees/`, { 
                  headers: officeHeaders,
                  signal: controller.signal
                });
                if (res.ok) {
                  const officeEmployees = await res.json();
                  allEmployees.push(...officeEmployees);
                }
              } catch (err) {
                console.error(`Error fetching employees for office ${office.id}:`, err);
              }
            }
            employeesData = allEmployees;
          }
        }
        
        setEmployees(employeesData);
        
        // Filter employees with badges
        const withBadges = employeesData.filter(emp => 
          emp.special_badges && emp.special_badges.length > 0
        );
        setEmployeesWithBadges(withBadges);
      } catch (empErr) {
        if (empErr.name === 'AbortError') {
          throw new Error('Request timeout - server took too long to respond');
        }
        console.error('Employee fetch error:', empErr);
        setEmployees([]);
        setEmployeesWithBadges([]);
      }
      
      // Fetch users
      let usersData = [];
      try {
        const usersRes = await fetch(`${API_URL}/api/users/`, { 
          headers,
          signal: controller.signal
        });
        if (usersRes.ok) {
          usersData = await usersRes.json();
          setUsers(usersData);
        } else {
          console.error('Failed to fetch users:', usersRes.status);
          setUsers([]);
        }
      } catch (userErr) {
        if (userErr.name === 'AbortError') {
          throw new Error('Request timeout - server took too long to respond');
        }
        console.error('User fetch error:', userErr);
        setUsers([]);
      }
      
      clearTimeout(timeoutId);

      // Show success if we got any data
      if (officesData.length > 0 || employeesData.length > 0 || usersData.length > 0) {
        setError(null); // Clear any previous error
      } else {
        // Auto-retry once if we got no data
        if (retryCountRef.current < 1) {
          retryCountRef.current++;
          console.log('No data received, retrying...');
          setTimeout(() => fetchData(), 1000);
          return;
        }
        // After retry, if still no data, that's okay - might just be empty
      }
      
    } catch (err) {
      console.error('Data fetch error:', err);
      // Auto-retry on network error (once)
      if (retryCountRef.current < 1) {
        retryCountRef.current++;
        console.log('Network error, retrying in 2 seconds...');
        setTimeout(() => fetchData(), 2000);
        return;
      }
      setError(`Network error: ${err.message}. Please click Retry or refresh the page.`);
    } finally {
      setLoading(false);
    }
  };

  // Orphan Data Functions
  const scanOrphanData = async () => {
    setOrphanLoading(true);
    setOrphanError(null);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/scan`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOrphanData(data);
        toast.success(`Scan complete: ${data.summary.total_orphans} orphan records found`);
      } else {
        const errorText = await res.text();
        setOrphanError(`Failed to scan: ${errorText}`);
      }
    } catch (err) {
      setOrphanError(`Scan error: ${err.message}`);
    } finally {
      setOrphanLoading(false);
    }
  };

  const runDiagnosis = async () => {
    setDiagnosisLoading(true);
    setOrphanError(null);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/diagnose`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDiagnosisData(data);
        toast.success(`Diagnosis complete: ${data.summary.issues_found} issues found`);
      } else {
        const errorText = await res.text();
        setOrphanError(`Diagnosis failed: ${errorText}`);
      }
    } catch (err) {
      setOrphanError(`Diagnosis error: ${err.message}`);
    } finally {
      setDiagnosisLoading(false);
    }
  };

  const deleteOrphanRecord = async (type, id, name) => {
    setProcessingOrphan(id);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/${type}/${id}`, {
        method: 'DELETE',
        headers
      });
      
      if (res.ok) {
        toast.success(`Deleted: ${name}`);
        // Refresh both orphan data and diagnosis if they were run
        if (orphanData) {
          await scanOrphanData();
        }
        if (diagnosisData) {
          await runDiagnosis();
        }
        // Also refresh main data
        await fetchData();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Delete failed');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setProcessingOrphan(null);
    }
  };

  const fixOrphanRecord = async (action, params) => {
    setProcessingOrphan(params.record_id || params.employee_id);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      let url;
      if (action === 'link-user') {
        url = `${API_URL}/api/orphan-data/fix/employee/${params.employee_id}/link-user/${params.user_id}`;
      } else if (action === 'clear-office') {
        url = `${API_URL}/api/orphan-data/fix/clear-invalid-office/${params.collection}/${params.record_id}`;
      }
      
      const res = await fetch(url, { method: 'POST', headers });
      
      if (res.ok) {
        toast.success('Record fixed successfully');
        await scanOrphanData();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Fix failed');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setProcessingOrphan(null);
    }
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllEmployees = () => {
    const filteredEmployees = getFilteredEmployees();
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(e => e.id));
    }
  };

  const handleSelectUser = (userId) => {
    // Don't allow selecting the current user or super admin
    const targetUser = users.find(u => u.id === userId);
    if (targetUser?.id === user?.id) return;
    
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    const filteredUsers = getFilteredUsers().filter(u => u.id !== user?.id);
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const openDeleteDialog = (type) => {
    setDeleteType(type);
    setConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    
    setProcessing(true);
    setError(null);
    setResult(null);
    
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      
      const idsToDelete = deleteType === 'employees' ? selectedEmployees : selectedUsers;
      const endpoint = deleteType === 'employees' 
        ? `${API_URL}/api/employees/bulk-delete`
        : `${API_URL}/api/users/bulk-delete`;
      
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ ids: idsToDelete })
      });
      
      if (res.ok) {
        const data = await res.json();
        setResult({ type: deleteType, count: data.deleted_count || idsToDelete.length });
        setShowDeleteDialog(false);
        setConfirmText('');
        
        // Clear selections and refresh
        if (deleteType === 'employees') {
          setSelectedEmployees([]);
        } else {
          setSelectedUsers([]);
        }
        fetchData();
      } else {
        const err = await res.json();
        setError(err.detail || `Failed to delete ${deleteType}`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getFilteredEmployees = () => {
    return employees.filter(emp => {
      const searchLower = employeeSearch.toLowerCase();
      const matchesSearch = !employeeSearch || 
        emp.first_name?.toLowerCase().includes(searchLower) ||
        emp.last_name?.toLowerCase().includes(searchLower) ||
        emp.contact?.email?.toLowerCase().includes(searchLower) ||
        emp.employment?.employee_id?.toLowerCase().includes(searchLower);
      
      const matchesOffice = selectedOffice === 'all' || emp.office_id === selectedOffice;
      
      return matchesSearch && matchesOffice;
    });
  };

  const getFilteredUsers = () => {
    return users.filter(u => {
      const searchLower = userSearch.toLowerCase();
      return !userSearch || 
        u.first_name?.toLowerCase().includes(searchLower) ||
        u.last_name?.toLowerCase().includes(searchLower) ||
        u.phone?.includes(searchLower);
    });
  };

  const getUserRole = (u) => {
    if (u.roles?.some(r => r.role === 'super_admin')) return 'Super Admin';
    if (u.roles?.some(r => r.role === 'office_admin')) return 'Office Admin';
    return 'Employee';
  };

  const getRoleBadge = (u) => {
    const role = getUserRole(u);
    if (role === 'Super Admin') return <Badge variant="destructive">{role}</Badge>;
    if (role === 'Office Admin') return <Badge variant="default">{role}</Badge>;
    return <Badge variant="secondary">{role}</Badge>;
  };

  // Badge management functions
  const getFilteredBadgeEmployees = () => {
    return employeesWithBadges.filter(emp => {
      const searchLower = badgeSearch.toLowerCase();
      const matchesSearch = !badgeSearch || 
        emp.first_name?.toLowerCase().includes(searchLower) ||
        emp.last_name?.toLowerCase().includes(searchLower) ||
        emp.contact?.email?.toLowerCase().includes(searchLower) ||
        emp.employment?.employee_id?.toLowerCase().includes(searchLower);
      
      const matchesOffice = selectedOffice === 'all' || emp.office_id === selectedOffice;
      
      return matchesSearch && matchesOffice;
    });
  };

  const handleSelectBadgeEmployee = (employeeId) => {
    setSelectedBadgeEmployees(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllBadgeEmployees = () => {
    const filteredEmployees = getFilteredBadgeEmployees();
    if (selectedBadgeEmployees.length === filteredEmployees.length) {
      setSelectedBadgeEmployees([]);
    } else {
      setSelectedBadgeEmployees(filteredEmployees.map(e => e.id));
    }
  };

  const handleClearBadges = async () => {
    if (clearBadgesConfirmText !== 'CLEAR') return;
    
    setProcessing(true);
    setError(null);
    setResult(null);
    
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      
      let clearedCount = 0;
      let failedCount = 0;
      
      // Clear badges for each selected employee
      for (const employeeId of selectedBadgeEmployees) {
        const employee = employeesWithBadges.find(e => e.id === employeeId);
        if (!employee?.special_badges || employee.special_badges.length === 0) continue;
        
        // Remove each badge from the employee
        for (const badge of employee.special_badges) {
          try {
            const res = await fetch(
              `${API_URL}/api/employees/${employeeId}/badges/${badge.badge_type}`,
              { method: 'DELETE', headers }
            );
            if (res.ok) {
              clearedCount++;
            } else {
              console.error(`Failed to remove badge ${badge.badge_type} from ${employeeId}:`, await res.text());
              failedCount++;
            }
          } catch (badgeErr) {
            console.error(`Error removing badge ${badge.badge_type}:`, badgeErr);
            failedCount++;
          }
        }
      }
      
      if (clearedCount > 0) {
        toast.success(`Successfully cleared ${clearedCount} badge(s) from ${selectedBadgeEmployees.length} employee(s)`);
        setResult({ type: 'badges', count: clearedCount });
      }
      
      if (failedCount > 0) {
        toast.error(`Failed to clear ${failedCount} badge(s)`);
      }
      
      setShowClearBadgesDialog(false);
      setClearBadgesConfirmText('');
      setSelectedBadgeEmployees([]);
      
      // Refresh data
      await fetchData();
      
    } catch (err) {
      console.error('Error clearing badges:', err);
      setError('Failed to clear badges. Please try again.');
      toast.error('Failed to clear badges');
    } finally {
      setProcessing(false);
    }
  };

  const clearSingleBadge = async (employeeId, badgeType) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(
        `${API_URL}/api/employees/${employeeId}/badges/${badgeType}`,
        { method: 'DELETE', headers }
      );
      
      if (res.ok) {
        toast.success(`Badge "${badgeType}" removed successfully`);
        // Wait for data refresh to complete
        await fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to remove badge');
      }
    } catch (err) {
      console.error('Error removing badge:', err);
      toast.error('Failed to remove badge');
    }
  };

  // Office deletion functions
  const previewOfficeDelete = async (officeId) => {
    setOfficeDeleteLoading(true);
    setOfficeDeletePreview(null);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/office/${officeId}/preview-delete`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOfficeDeletePreview(data);
        toast.success(`Preview loaded for ${data.office.name}`);
      } else {
        const errorText = await res.text();
        toast.error(`Preview failed: ${errorText}`);
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setOfficeDeleteLoading(false);
    }
  };

  const handleDeleteOffice = async () => {
    if (deleteOfficeConfirmText !== 'DELETE OFFICE' || !selectedDeleteOffice) return;
    
    setProcessing(true);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(
        `${API_URL}/api/orphan-data/office/${selectedDeleteOffice}/full-delete`,
        { method: 'DELETE', headers }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowDeleteOfficeDialog(false);
        setDeleteOfficeConfirmText('');
        setSelectedDeleteOffice(null);
        setOfficeDeletePreview(null);
        // Refresh data
        await fetchData();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Failed to delete office');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Check for office-employee mismatches (helps diagnose why employees don't appear)
  const checkOfficeMismatch = async () => {
    setMismatchLoading(true);
    setMismatchData(null);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/office-employee-mismatch`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMismatchData(data);
        if (data.mismatches?.length > 0) {
          toast.error(`Found ${data.mismatches.length} mismatches!`);
        } else {
          toast.success('No office-employee mismatches found');
        }
      } else {
        toast.error('Failed to check mismatches');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setMismatchLoading(false);
    }
  };

  // Fix employee's office_id
  const fixEmployeeOffice = async (employeeId, newOfficeId) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(
        `${API_URL}/api/orphan-data/fix-employee-office?employee_id=${employeeId}&new_office_id=${newOfficeId}`,
        { method: 'POST', headers }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Refresh mismatch data
        await checkOfficeMismatch();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Failed to fix employee office');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  // Search for employees by office name
  const searchOfficeEmployees = async () => {
    if (!officeSearchName.trim()) {
      toast.error('Please enter an office name');
      return;
    }
    
    setOfficeSearchLoading(true);
    setOfficeSearchResult(null);
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(
        `${API_URL}/api/orphan-data/find-employees-for-office?office_name=${encodeURIComponent(officeSearchName)}`,
        { headers }
      );
      
      if (res.ok) {
        const data = await res.json();
        setOfficeSearchResult(data);
        
        if (data.office_found && data.employees_in_this_office.length === 0) {
          toast.warning(`Office found but has 0 employees! Check where your employees actually are.`);
        } else if (data.office_found) {
          toast.success(`Found ${data.employees_in_this_office.length} employees in ${data.office_found.name}`);
        } else {
          toast.error('Office not found');
        }
      } else {
        toast.error('Search failed');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setOfficeSearchLoading(false);
    }
  };

  // Reassign employees to correct office
  const reassignEmployees = async (employeeIds, targetOfficeId) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const res = await fetch(`${API_URL}/api/orphan-data/reassign-employees-to-office`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          employee_ids: employeeIds,
          target_office_id: targetOfficeId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Refresh search
        await searchOfficeEmployees();
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || 'Failed to reassign employees');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  // Diagnostic Console - Full system diagnosis
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDiagnosticLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const runFullDiagnosis = async () => {
    setDiagnosticLoading(true);
    setDiagnosticLogs([]);
    setDiagnosticData(null);
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    const data = {
      offices: [],
      officeAdmins: [],
      employees: [],
      employeesByOffice: {},
      issues: []
    };
    
    try {
      // Step 1: Get all offices
      addLog('Fetching all offices...', 'info');
      const officesRes = await fetch(`${API_URL}/api/offices/`, { headers });
      if (officesRes.ok) {
        data.offices = await officesRes.json();
        addLog(`✓ Found ${data.offices.length} offices`, 'success');
        data.offices.forEach(o => {
          addLog(`  Office: "${o.name}" → ID: ${o.id}`, 'info');
        });
      } else {
        addLog('✗ Failed to fetch offices', 'error');
      }
      
      // Step 2: Get office-employee mismatch data
      addLog('Checking office-employee relationships...', 'info');
      const mismatchRes = await fetch(`${API_URL}/api/orphan-data/office-employee-mismatch`, { headers });
      if (mismatchRes.ok) {
        const mismatchData = await mismatchRes.json();
        data.officeAdmins = mismatchData.office_admins || [];
        data.employeesByOffice = mismatchData.employees_by_office || {};
        
        addLog(`✓ Found ${data.officeAdmins.length} office admins`, 'success');
        data.officeAdmins.forEach(admin => {
          const status = admin.office_exists ? '✓' : '✗ INVALID';
          const logType = admin.office_exists ? 'info' : 'error';
          addLog(`  Admin: "${admin.name}" (${admin.phone}) → Office: ${admin.assigned_office_id} ${status}`, logType);
          
          if (!admin.office_exists) {
            data.issues.push({
              type: 'invalid_admin_office',
              message: `Office Admin "${admin.name}" has invalid office_id: ${admin.assigned_office_id}`,
              admin
            });
          }
        });
        
        // Check employees by office
        addLog('Checking employee office assignments...', 'info');
        Object.entries(data.employeesByOffice).forEach(([officeId, info]) => {
          const status = info.office_exists ? '✓' : '✗ INVALID OFFICE';
          const logType = info.office_exists ? 'success' : 'error';
          addLog(`  Office "${info.office_name || officeId}": ${info.employees?.length || 0} employees ${status}`, logType);
          
          if (!info.office_exists && info.employees?.length > 0) {
            data.issues.push({
              type: 'employees_in_invalid_office',
              message: `${info.employees.length} employees assigned to non-existent office: ${officeId}`,
              employees: info.employees,
              invalidOfficeId: officeId
            });
          }
        });
        
        if (mismatchData.mismatches?.length > 0) {
          mismatchData.mismatches.forEach(m => {
            addLog(`⚠ ${m.description}`, 'warning');
          });
        }
      }
      
      // Step 3: Check each office for employee visibility
      addLog('Testing employee visibility for each office...', 'info');
      for (const office of data.offices) {
        const empRes = await fetch(`${API_URL}/api/employees/`, {
          headers: { ...headers, 'X-Office-ID': office.id }
        });
        if (empRes.ok) {
          const employees = await empRes.json();
          const expectedCount = data.employeesByOffice[office.id]?.employees?.length || 0;
          
          if (employees.length === expectedCount) {
            addLog(`  ✓ "${office.name}": ${employees.length} employees (matches expected)`, 'success');
          } else if (employees.length === 0 && expectedCount > 0) {
            addLog(`  ✗ "${office.name}": API returns 0 employees but expected ${expectedCount}!`, 'error');
            data.issues.push({
              type: 'visibility_mismatch',
              message: `Office "${office.name}" should have ${expectedCount} employees but API returns 0`,
              office,
              expectedCount
            });
          } else {
            addLog(`  ? "${office.name}": ${employees.length} employees (expected ${expectedCount})`, 'warning');
          }
        }
      }
      
      // Step 4: Debug visibility endpoint
      addLog('Running debug visibility check...', 'info');
      const debugRes = await fetch(`${API_URL}/api/employees/debug-visibility`, { headers });
      if (debugRes.ok) {
        const debugData = await debugRes.json();
        addLog(`✓ Debug check complete - ${debugData.summary?.total_issues || 0} issues found`, 
          debugData.summary?.total_issues > 0 ? 'warning' : 'success');
        
        if (debugData.issues_detected?.length > 0) {
          debugData.issues_detected.forEach(issue => {
            addLog(`  ⚠ ${issue}`, 'warning');
            data.issues.push({ type: 'debug_issue', message: issue });
          });
        }
      }
      
      // Summary
      addLog('─'.repeat(50), 'info');
      addLog(`DIAGNOSIS COMPLETE`, 'info');
      addLog(`Total Offices: ${data.offices.length}`, 'info');
      addLog(`Total Office Admins: ${data.officeAdmins.length}`, 'info');
      addLog(`Total Issues Found: ${data.issues.length}`, data.issues.length > 0 ? 'error' : 'success');
      
      if (data.issues.length > 0) {
        addLog('─'.repeat(50), 'info');
        addLog('ISSUES REQUIRING ATTENTION:', 'error');
        data.issues.forEach((issue, idx) => {
          addLog(`${idx + 1}. ${issue.message}`, 'error');
        });
      }
      
      setDiagnosticData(data);
      
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    } finally {
      setDiagnosticLoading(false);
    }
  };

  // Test specific office API call
  const testOfficeApi = async (officeId, officeName) => {
    if (!officeId) {
      addLog('✗ Please enter an office ID', 'error');
      return;
    }
    
    addLog(`─`.repeat(50), 'info');
    addLog(`Testing API for office: ${officeName || officeId}`, 'info');
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-Office-ID': officeId
    };
    
    try {
      // Test employees endpoint
      addLog(`Calling /api/employees/ with X-Office-ID: ${officeId}`, 'info');
      
      const response = await fetch(`${API_URL}/api/employees/`, { headers });
      addLog(`Response status: ${response.status}`, response.ok ? 'success' : 'error');
      
      if (response.ok) {
        const employees = await response.json();
        addLog(`✓ Returned ${employees.length} employees`, 'success');
        
        if (employees.length > 0) {
          addLog(`Employees:`, 'info');
          employees.forEach(emp => {
            addLog(`  • ${emp.first_name} ${emp.last_name} (${emp.employment?.employee_id || 'N/A'}) - office_id: ${emp.office_id}`, 'info');
          });
        } else {
          addLog(`⚠ No employees returned. Checking database directly...`, 'warning');
          
          // Check what's in the mismatch data for this office
          const mismatchRes = await fetch(`${API_URL}/api/orphan-data/office-employee-mismatch`, {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
          });
          if (mismatchRes.ok) {
            const mismatchData = await mismatchRes.json();
            const officeData = mismatchData.employees_by_office?.[officeId];
            if (officeData?.employees?.length > 0) {
              addLog(`✗ DATABASE HAS ${officeData.employees.length} EMPLOYEES but API returns 0!`, 'error');
              addLog(`Employees in database:`, 'warning');
              officeData.employees.forEach(emp => {
                addLog(`  • ${emp.name} (${emp.employee_id})`, 'warning');
              });
              addLog(`This is likely a query or permission issue.`, 'error');
            } else {
              addLog(`Database also shows 0 employees for this office_id`, 'info');
            }
          }
        }
      } else {
        const errorText = await response.text();
        addLog(`✗ API Error: ${errorText}`, 'error');
      }
      
    } catch (err) {
      addLog(`✗ Fetch Error: ${err.message}`, 'error');
      addLog(`This usually means the API crashed. Checking raw data...`, 'warning');
      
      // Try to get raw employee data to find the problem
      try {
        const rawRes = await fetch(`${API_URL}/api/orphan-data/raw-employees/${officeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (rawRes.ok) {
          const rawData = await rawRes.json();
          addLog(`Raw data check: Found ${rawData.total} employees in database`, 'info');
          
          if (rawData.has_errors) {
            addLog(`⚠ FOUND DATA ISSUES:`, 'error');
            rawData.errors.forEach(err => {
              addLog(`  Employee: ${err.name} (${err.employee_id})`, 'error');
              err.issues.forEach(issue => {
                addLog(`    - ${issue}`, 'error');
              });
            });
          } else {
            addLog(`No obvious data issues found. Listing employees:`, 'info');
            rawData.employees.forEach(emp => {
              addLog(`  • ${emp.first_name} ${emp.last_name} - fields: ${emp.fields.join(', ')}`, 'info');
            });
          }
        }
      } catch (rawErr) {
        addLog(`Could not check raw data: ${rawErr.message}`, 'error');
      }
    }
  };

  // Quick test buttons for specific scenarios
  const testRefillOffice = () => {
    testOfficeApi('2c83cda4-fff1-46f0-a97a-3909abe334b6', 'REFILL ECOWATER LIMITED');
  };

  // BULLETPROOF TEST - Catches the exact error causing API crashes
  const runBulletproofTest = async (officeId, officeName) => {
    addLog(`─`.repeat(50), 'info');
    addLog(`🔒 BULLETPROOF TEST for: ${officeName || officeId}`, 'info');
    addLog(`This test will NEVER crash - it catches all errors internally.`, 'info');
    
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/bulletproof-employee-test/${officeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        addLog(`\n📊 RESULTS:`, 'info');
        addLog(`  Success: ${data.success ? '✅ YES' : '❌ NO'}`, data.success ? 'success' : 'error');
        addLog(`  Employees found in DB: ${data.employees_found}`, 'info');
        addLog(`  Employees processed: ${data.employees_processed}`, 'info');
        
        if (data.debug_info) {
          addLog(`\n🔍 DEBUG INFO:`, 'info');
          addLog(`  Last step: ${data.debug_info.step || 'N/A'}`, 'info');
          if (data.debug_info.sub_step) {
            addLog(`  Sub-step: ${data.debug_info.sub_step}`, 'info');
          }
          if (data.debug_info.message) {
            addLog(`  Message: ${data.debug_info.message}`, 'info');
          }
        }
        
        if (!data.success) {
          addLog(`\n❌ ERROR DETAILS:`, 'error');
          addLog(`  Error Type: ${data.error_type}`, 'error');
          addLog(`  Error Message: ${data.error_message}`, 'error');
          
          if (data.failed_at_employee) {
            addLog(`\n🎯 FAILED AT EMPLOYEE:`, 'error');
            addLog(`  Index: ${data.failed_at_employee.index}`, 'error');
            addLog(`  ID: ${data.failed_at_employee.id}`, 'error');
            addLog(`  Name: ${data.failed_at_employee.first_name} ${data.failed_at_employee.last_name}`, 'error');
            addLog(`  Employee ID: ${data.failed_at_employee.employee_id}`, 'error');
            addLog(`  Raw doc keys: ${data.failed_at_employee.raw_doc_keys?.join(', ')}`, 'warning');
            addLog(`  Employment keys: ${data.failed_at_employee.employment_keys?.join(', ')}`, 'warning');
          }
          
          if (data.failed_employee_raw_data) {
            addLog(`\n📄 RAW DATA OF FAILED EMPLOYEE:`, 'warning');
            Object.entries(data.failed_employee_raw_data).forEach(([key, value]) => {
              addLog(`  ${key}: ${value}`, 'warning');
            });
          }
          
          if (data.error_traceback) {
            addLog(`\n📜 FULL TRACEBACK:`, 'error');
            data.error_traceback.split('\n').forEach(line => {
              if (line.trim()) addLog(`  ${line}`, 'error');
            });
          }
        }
        
        // Store for potential fixes
        if (data.failed_at_employee) {
          setDiagnosticData(prev => ({
            ...prev,
            bulletproofFailure: data
          }));
        }
        
      } else {
        addLog(`✗ Endpoint returned error: ${response.status}`, 'error');
        const text = await response.text();
        addLog(`  ${text}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Network/Fetch error: ${err.message}`, 'error');
    }
  };

  // Fix employee None values
  const fixEmployeeNoneValues = async (employeeId, employeeName) => {
    addLog(`Fixing None values for: ${employeeName} (${employeeId})`, 'info');
    
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/fix-employee-none-values/${employeeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✓ ${data.message}`, 'success');
        if (data.fixed_fields.length > 0) {
          data.fixed_fields.forEach(f => addLog(`  Fixed: ${f}`, 'success'));
        }
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  // Delete employee
  const deleteEmployeeDirect = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to DELETE employee "${employeeName}" (${employeeId})? This cannot be undone!`)) {
      return;
    }
    
    addLog(`Deleting employee: ${employeeName} (${employeeId})`, 'warning');
    
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/delete-employee/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✓ ${data.message}`, 'success');
        toast.success(data.message);
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  // Check for recreated/orphan data issues across all offices
  const checkRecreatedData = async () => {
    addLog(`─`.repeat(50), 'info');
    addLog(`🔍 CHECKING FOR RECREATED/ORPHAN DATA ISSUES...`, 'info');
    
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/check-recreated-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        addLog(`\n📊 SUMMARY:`, 'info');
        addLog(`  Duplicate phone groups: ${data.summary.duplicate_phone_groups}`, data.summary.duplicate_phone_groups > 0 ? 'warning' : 'success');
        addLog(`  Orphan user references: ${data.summary.orphan_user_references}`, data.summary.orphan_user_references > 0 ? 'error' : 'success');
        addLog(`  Orphan manager references: ${data.summary.orphan_manager_references}`, data.summary.orphan_manager_references > 0 ? 'error' : 'success');
        addLog(`  Invalid office references: ${data.summary.employees_with_invalid_offices}`, data.summary.employees_with_invalid_offices > 0 ? 'error' : 'success');
        
        if (data.duplicate_phones.length > 0) {
          addLog(`\n📞 DUPLICATE PHONE NUMBERS:`, 'warning');
          data.duplicate_phones.forEach(dup => {
            addLog(`  Phone ${dup.phone} used by ${dup.count} employees:`, 'warning');
            dup.employees.forEach(emp => {
              addLog(`    • ${emp.name} (${emp.id}) in office ${emp.office_id}`, 'warning');
            });
          });
        }
        
        if (data.orphan_user_references.length > 0) {
          addLog(`\n👤 ORPHAN USER REFERENCES (user deleted but employee still references):`, 'error');
          data.orphan_user_references.forEach(ref => {
            addLog(`  ✗ ${ref.employee_name} (${ref.employee_id})`, 'error');
            addLog(`    Points to non-existent user: ${ref.invalid_user_id}`, 'error');
          });
        }
        
        if (data.orphan_manager_references.length > 0) {
          addLog(`\n👔 ORPHAN MANAGER REFERENCES (manager deleted but employee still references):`, 'error');
          data.orphan_manager_references.forEach(ref => {
            addLog(`  ✗ ${ref.employee_name} (${ref.employee_id})`, 'error');
            addLog(`    Points to non-existent manager: ${ref.invalid_manager_id}`, 'error');
          });
        }
        
        if (data.employees_with_invalid_offices.length > 0) {
          addLog(`\n🏢 EMPLOYEES WITH INVALID OFFICE (office deleted/recreated):`, 'error');
          data.employees_with_invalid_offices.forEach(emp => {
            addLog(`  ✗ ${emp.employee_name} (${emp.employee_id})`, 'error');
            addLog(`    Points to non-existent office: ${emp.invalid_office_id}`, 'error');
          });
        }
        
        // Store for fix buttons
        setDiagnosticData(prev => ({
          ...prev,
          recreatedDataIssues: data
        }));
        
        if (data.summary.total_issues === 0) {
          addLog(`\n✅ No orphan/recreated data issues found!`, 'success');
        } else {
          addLog(`\n⚠️ Found ${data.summary.total_issues} issues that may prevent employee loading!`, 'error');
        }
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  // Fix orphan user reference
  const fixOrphanUserRef = async (employeeId, employeeName) => {
    addLog(`Fixing orphan user reference for: ${employeeName}`, 'info');
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/fix-orphan-user-reference/${employeeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        addLog(`✓ Fixed! User reference cleared.`, 'success');
        toast.success('Fixed orphan user reference');
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  // Fix orphan manager reference
  const fixOrphanManagerRef = async (employeeId, employeeName) => {
    addLog(`Fixing orphan manager reference for: ${employeeName}`, 'info');
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/fix-orphan-manager-reference/${employeeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        addLog(`✓ Fixed! Manager reference cleared.`, 'success');
        toast.success('Fixed orphan manager reference');
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  // Check raw employee data for an office
  const checkRawEmployeeData = async (officeId, officeName) => {
    addLog(`─`.repeat(50), 'info');
    addLog(`Checking raw employee data for: ${officeName || officeId}`, 'info');
    
    try {
      const response = await fetch(`${API_URL}/api/orphan-data/raw-employees/${officeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        addLog(`✓ Found ${data.total} employees`, 'success');
        
        // Show orphan issues
        if (data.orphan_issues?.length > 0) {
          addLog(`\n⚠️ ORPHAN REFERENCE ISSUES:`, 'error');
          data.orphan_issues.forEach(issue => {
            addLog(`  ✗ ${issue.employee_name}: ${issue.type}`, 'error');
            if (issue.invalid_user_id) addLog(`    Invalid user_id: ${issue.invalid_user_id}`, 'error');
            if (issue.invalid_manager_id) addLog(`    Invalid manager_id: ${issue.invalid_manager_id}`, 'error');
          });
        }
        
        // Show user issues
        if (data.user_issues?.length > 0) {
          addLog(`\n⚠️ USER WITHOUT EMPLOYEE RECORD:`, 'warning');
          data.user_issues.forEach(issue => {
            addLog(`  ✗ ${issue.name} (${issue.phone}): ${issue.message}`, 'warning');
          });
        }
        
        if (data.has_errors) {
          addLog(`\n⚠ DATA ISSUES FOUND:`, 'error');
          data.errors.forEach(err => {
            addLog(`  Employee: ${err.name} (${err.employee_id})`, 'error');
            err.issues.forEach(issue => {
              addLog(`    ✗ ${issue}`, 'error');
            });
          });
        }
        
        // Store problematic employees for fix buttons
        setDiagnosticData(prev => ({
          ...prev,
          problematicEmployees: data.employees.filter(e => e.issues.length > 0).map(e => ({
            id: e.id,
            name: `${e.first_name} ${e.last_name}`,
            issues: e.issues
          }))
        }));
        
        addLog(`Employee details:`, 'info');
        data.employees.forEach(emp => {
          const status = emp.issues.length > 0 ? '❌' : '✅';
          addLog(`  ${status} ${emp.first_name} ${emp.last_name}`, emp.issues.length > 0 ? 'error' : 'success');
          addLog(`     ID: ${emp.id}`, 'info');
          addLog(`     Fields: ${emp.fields.join(', ')}`, 'info');
          if (emp.employment_fields) {
            addLog(`     Employment: ${emp.employment_fields.join(', ')}`, 'info');
          }
          if (emp.issues.length > 0) {
            emp.issues.forEach(issue => addLog(`     ⚠ ${issue}`, 'warning'));
          }
        });
      } else {
        addLog(`✗ Failed: ${await response.text()}`, 'error');
      }
    } catch (err) {
      addLog(`✗ Error: ${err.message}`, 'error');
    }
  };

  const getBadgeColor = (badgeType) => {
    switch (badgeType) {
      case 'reviewer': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'approver': return 'bg-green-100 text-green-800 border-green-300';
      case 'authorizer': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Wait for user data to load before checking permissions
  if (!user) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!isSuperAdmin()) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Access denied. Super Admin privileges required.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const filteredEmployees = getFilteredEmployees();
  const filteredUsers = getFilteredUsers();

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Database className="h-6 w-6" />
            Data Cleanup
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage and delete employees and users</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button 
          variant="outline" 
          onClick={checkOfficeMismatch} 
          disabled={mismatchLoading}
          className="border-orange-300 hover:bg-orange-50"
          data-testid="check-mismatch-btn"
        >
          {mismatchLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
          )}
          Check Mismatches
        </Button>
        <Button 
          variant="outline" 
          onClick={() => setShowDiagnosticConsole(true)}
          className="border-purple-300 hover:bg-purple-50"
          data-testid="diagnostic-console-btn"
        >
          <Terminal className="h-4 w-4 mr-2 text-purple-600" />
          Diagnostic Console
        </Button>
      </div>

      {/* Diagnostic Console Modal */}
      <Dialog open={showDiagnosticConsole} onOpenChange={setShowDiagnosticConsole}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5 text-purple-600" />
              Diagnostic Console
            </DialogTitle>
            <DialogDescription>
              Run comprehensive system diagnostics to identify employee visibility issues
            </DialogDescription>
          </DialogHeader>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mb-2">
            <Button onClick={runFullDiagnosis} disabled={diagnosticLoading} className="bg-purple-600 hover:bg-purple-700">
              {diagnosticLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Full Diagnosis
            </Button>
            <Button 
              variant="outline"
              onClick={checkRecreatedData}
              className="border-purple-400 hover:bg-purple-50"
            >
              🔄 Check Recreated/Orphan Data
            </Button>
            <Button 
              variant="outline"
              onClick={testRefillOffice}
              className="border-red-300 hover:bg-red-50"
            >
              🔍 Test REFILL API
            </Button>
            <Button 
              variant="outline"
              onClick={() => checkRawEmployeeData('2c83cda4-fff1-46f0-a97a-3909abe334b6', 'REFILL ECOWATER LIMITED')}
              className="border-yellow-400 hover:bg-yellow-50"
            >
              🔬 Check REFILL Raw Data
            </Button>
            <Button 
              variant="default"
              onClick={() => runBulletproofTest('2c83cda4-fff1-46f0-a97a-3909abe334b6', 'REFILL ECOWATER LIMITED')}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulletproof-test-btn"
            >
              🔒 BULLETPROOF Test REFILL
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                const logText = diagnosticLogs.map(l => `[${l.timestamp}] ${l.message}`).join('\n');
                navigator.clipboard.writeText(logText);
                toast.success('Logs copied to clipboard');
              }}
              disabled={diagnosticLogs.length === 0}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Logs
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setDiagnosticLogs([])}
              disabled={diagnosticLogs.length === 0}
            >
              Clear
            </Button>
          </div>
          
          {/* Test Specific Office */}
          <div className="flex gap-2 mb-4 p-3 bg-gray-100 rounded-lg">
            <Input
              placeholder="Enter office ID to test..."
              value={testOfficeId}
              onChange={(e) => setTestOfficeId(e.target.value)}
              className="flex-1 font-mono text-sm"
            />
            <Button 
              variant="outline"
              onClick={() => testOfficeApi(testOfficeId, '')}
              disabled={!testOfficeId}
            >
              Test Office
            </Button>
            <Button 
              variant="default"
              onClick={() => runBulletproofTest(testOfficeId, '')}
              disabled={!testOfficeId}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              🔒 Bulletproof
            </Button>
            <Select onValueChange={(v) => {
              const office = offices.find(o => o.id === v);
              if (office) {
                setTestOfficeId(v);
                testOfficeApi(v, office.name);
              }
            }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Or select office..." />
              </SelectTrigger>
              <SelectContent>
                {offices.map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Console Output */}
          <div className="flex-1 bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-y-auto min-h-[300px]">
            {diagnosticLogs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                Click "Run Full Diagnosis" or select an office to test...
              </div>
            ) : (
              diagnosticLogs.map((log, idx) => (
                <div 
                  key={idx} 
                  className={`py-0.5 ${
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'warning' ? 'text-yellow-400' :
                    log.type === 'success' ? 'text-green-400' :
                    'text-gray-300'
                  }`}
                >
                  <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                </div>
              ))
            )}
            {diagnosticLoading && (
              <div className="text-purple-400 animate-pulse">
                ▌ Running...
              </div>
            )}
          </div>
          
          {/* Problematic Employees - Quick Fix Buttons */}
          {diagnosticData?.problematicEmployees?.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Problematic Employees - Fix Options:
              </h4>
              <div className="space-y-2">
                {diagnosticData.problematicEmployees.map((emp, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                    <div>
                      <span className="font-medium">{emp.name}</span>
                      <span className="text-xs text-gray-500 ml-2">({emp.id})</span>
                      <div className="text-xs text-red-600">{emp.issues.join(', ')}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => fixEmployeeNoneValues(emp.id, emp.name)}
                        className="text-xs"
                      >
                        🔧 Fix None Values
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => deleteEmployeeDirect(emp.id, emp.name)}
                        className="text-xs"
                      >
                        🗑️ Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                Note: DANIEL KAUTA appears twice. You may want to delete the duplicate (9027a91d-...).
              </p>
            </div>
          )}
          
          {/* Bulletproof Test Failure - Shows exact crash details */}
          {diagnosticData?.bulletproofFailure && !diagnosticData.bulletproofFailure.success && (
            <div className="mt-4 p-3 bg-red-100 border-2 border-red-500 rounded-lg">
              <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2">
                🔒 BULLETPROOF TEST FAILURE - Exact Error Found!
              </h4>
              <div className="space-y-2 text-sm">
                <div className="bg-white p-2 rounded">
                  <p><strong>Error Type:</strong> <code className="bg-red-200 px-1 rounded">{diagnosticData.bulletproofFailure.error_type}</code></p>
                  <p><strong>Error Message:</strong> {diagnosticData.bulletproofFailure.error_message}</p>
                </div>
                
                {diagnosticData.bulletproofFailure.failed_at_employee && (
                  <div className="bg-yellow-50 p-2 rounded border border-yellow-300">
                    <p className="font-semibold text-yellow-800">🎯 Failed at Employee:</p>
                    <p>Name: <strong>{diagnosticData.bulletproofFailure.failed_at_employee.first_name} {diagnosticData.bulletproofFailure.failed_at_employee.last_name}</strong></p>
                    <p>ID: <code className="bg-gray-200 px-1 rounded text-xs">{diagnosticData.bulletproofFailure.failed_at_employee.id}</code></p>
                    <p>Employee ID: {diagnosticData.bulletproofFailure.failed_at_employee.employee_id || 'N/A'}</p>
                    <div className="mt-2 flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => fixEmployeeNoneValues(
                          diagnosticData.bulletproofFailure.failed_at_employee.id,
                          `${diagnosticData.bulletproofFailure.failed_at_employee.first_name} ${diagnosticData.bulletproofFailure.failed_at_employee.last_name}`
                        )}
                      >
                        🔧 Fix None Values
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => deleteEmployeeDirect(
                          diagnosticData.bulletproofFailure.failed_at_employee.id,
                          `${diagnosticData.bulletproofFailure.failed_at_employee.first_name} ${diagnosticData.bulletproofFailure.failed_at_employee.last_name}`
                        )}
                      >
                        🗑️ Delete This Employee
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recreated Data Issues - Fix Buttons */}
          {diagnosticData?.recreatedDataIssues && (diagnosticData.recreatedDataIssues.summary?.total_issues > 0) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Orphan/Recreated Data Issues - Fix Options:
              </h4>
              
              {/* Orphan User References */}
              {diagnosticData.recreatedDataIssues.orphan_user_references?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Orphan User References:</p>
                  {diagnosticData.recreatedDataIssues.orphan_user_references.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border mb-1">
                      <span className="text-sm">{ref.employee_name} → invalid user: {ref.invalid_user_id.substring(0,8)}...</span>
                      <Button size="sm" variant="outline" onClick={() => fixOrphanUserRef(ref.employee_id, ref.employee_name)}>
                        🔧 Clear Reference
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Orphan Manager References */}
              {diagnosticData.recreatedDataIssues.orphan_manager_references?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-red-700 mb-1">Orphan Manager References:</p>
                  {diagnosticData.recreatedDataIssues.orphan_manager_references.map((ref, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border mb-1">
                      <span className="text-sm">{ref.employee_name} → invalid manager: {ref.invalid_manager_id.substring(0,8)}...</span>
                      <Button size="sm" variant="outline" onClick={() => fixOrphanManagerRef(ref.employee_id, ref.employee_name)}>
                        🔧 Clear Reference
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Duplicate Phones */}
              {diagnosticData.recreatedDataIssues.duplicate_phones?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-yellow-700 mb-1">Duplicate Phone Numbers:</p>
                  {diagnosticData.recreatedDataIssues.duplicate_phones.map((dup, idx) => (
                    <div key={idx} className="bg-yellow-50 p-2 rounded border border-yellow-200 mb-1 text-sm">
                      <p>Phone {dup.phone} used by {dup.count} employees:</p>
                      <ul className="ml-4 text-xs">
                        {dup.employees.map((emp, i) => (
                          <li key={i} className="flex items-center justify-between">
                            <span>{emp.name}</span>
                            <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" onClick={() => deleteEmployeeDirect(emp.id, emp.name)}>
                              Delete
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Issues Summary */}
          {diagnosticData?.issues?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="font-semibold text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {diagnosticData.issues.length} Issue(s) Found - Quick Fixes:
              </h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {diagnosticData.issues.map((issue, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded p-2 text-sm">
                    <p className="text-red-800">{issue.message}</p>
                    {issue.type === 'employees_in_invalid_office' && issue.employees && (
                      <div className="mt-2 flex gap-2">
                        <Select onValueChange={(officeId) => {
                          if (window.confirm(`Move ${issue.employees.length} employees to selected office?`)) {
                            reassignEmployees(issue.employees.map(e => e.id), officeId);
                          }
                        }}>
                          <SelectTrigger className="w-64 h-8 text-xs">
                            <SelectValue placeholder="Move to office..." />
                          </SelectTrigger>
                          <SelectContent>
                            {offices.map(o => (
                              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDiagnosticConsole(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Office Employee Search - for debugging specific office */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-md flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Find Employees by Office Name
          </CardTitle>
          <CardDescription>Search for an office to see which employees are assigned to it</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Enter office name (e.g., REFILL ECOWATER LIMITED)"
              value={officeSearchName}
              onChange={(e) => setOfficeSearchName(e.target.value)}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && searchOfficeEmployees()}
            />
            <Button onClick={searchOfficeEmployees} disabled={officeSearchLoading}>
              {officeSearchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Search Results */}
          {officeSearchResult && (
            <div className="mt-4 space-y-3">
              {/* Office Found */}
              {officeSearchResult.office_found ? (
                <Alert className={officeSearchResult.employees_in_this_office.length === 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}>
                  <Building className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Office Found:</strong> {officeSearchResult.office_found.name}<br />
                    <strong>Office ID:</strong> <code className="bg-gray-200 px-1 rounded">{officeSearchResult.office_found.id}</code><br />
                    <strong>Employees in this office:</strong> {officeSearchResult.employees_in_this_office.length}
                    {officeSearchResult.employees_in_this_office.length === 0 && (
                      <span className="text-red-600 font-bold"> ⚠️ NO EMPLOYEES!</span>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Office "{officeSearchResult.search_term}" not found!</AlertDescription>
                </Alert>
              )}
              
              {/* Likely Issue */}
              {officeSearchResult.likely_issue && (
                <Alert className="border-yellow-400 bg-yellow-50">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-800">
                    <strong>Likely Issue:</strong> {officeSearchResult.likely_issue}
                  </AlertDescription>
                </Alert>
              )}
              
              {/* All Employees Table */}
              {officeSearchResult.all_employees_with_offices?.length > 0 && (
                <div className="bg-white rounded border">
                  <div className="p-2 bg-gray-100 font-medium text-sm border-b flex justify-between items-center">
                    <span>All Employees ({officeSearchResult.all_employees_with_offices.length})</span>
                    {officeSearchResult.office_found && officeSearchResult.employees_in_this_office.length === 0 && (
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => {
                          const wrongEmployees = officeSearchResult.all_employees_with_offices.filter(
                            e => e.office_id !== officeSearchResult.office_found.id
                          );
                          if (wrongEmployees.length > 0 && window.confirm(`Move ${wrongEmployees.length} employees to ${officeSearchResult.office_found.name}?`)) {
                            reassignEmployees(wrongEmployees.map(e => e.id), officeSearchResult.office_found.id);
                          }
                        }}
                      >
                        Fix: Move All to {officeSearchResult.office_found?.name}
                      </Button>
                    )}
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Employee</th>
                          <th className="px-3 py-2 text-left">Current Office</th>
                          <th className="px-3 py-2 text-left">Office ID</th>
                          <th className="px-3 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {officeSearchResult.all_employees_with_offices.map(emp => (
                          <tr key={emp.id} className={emp.office_id === officeSearchResult.office_found?.id ? 'bg-green-50' : 'bg-red-50'}>
                            <td className="px-3 py-2">
                              <div className="font-medium">{emp.name}</div>
                              <div className="text-xs text-gray-500">{emp.employee_id}</div>
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant={emp.office_id === officeSearchResult.office_found?.id ? "default" : "destructive"}>
                                {emp.office_name}
                              </Badge>
                            </td>
                            <td className="px-3 py-2">
                              <code className="text-xs bg-gray-100 px-1 rounded">{emp.office_id}</code>
                            </td>
                            <td className="px-3 py-2">
                              {emp.office_id !== officeSearchResult.office_found?.id && officeSearchResult.office_found && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => reassignEmployees([emp.id], officeSearchResult.office_found.id)}
                                >
                                  Move to {officeSearchResult.office_found.name}
                                </Button>
                              )}
                              {emp.office_id === officeSearchResult.office_found?.id && (
                                <span className="text-green-600 text-xs">✓ Correct</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setError(null);
                fetchData();
              }}
              className="ml-4 bg-white hover:bg-gray-100"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {result && (
        <Alert className="border-green-500 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Successfully deleted {result.count} {result.type}.
          </AlertDescription>
        </Alert>
      )}

      {/* Office-Employee Mismatch Results */}
      {mismatchData && (
        <Card className={mismatchData.mismatches?.length > 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              {mismatchData.mismatches?.length > 0 ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
              Office-Employee Mismatch Check
              <Button variant="ghost" size="sm" onClick={() => setMismatchData(null)} className="ml-auto">
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-3 text-center">
              <div className="bg-white rounded p-2 border">
                <p className="text-xl font-bold">{mismatchData.summary?.total_offices}</p>
                <p className="text-xs text-gray-500">Offices</p>
              </div>
              <div className="bg-white rounded p-2 border">
                <p className="text-xl font-bold">{mismatchData.summary?.total_office_admins}</p>
                <p className="text-xs text-gray-500">Office Admins</p>
              </div>
              <div className="bg-white rounded p-2 border">
                <p className="text-xl font-bold">{mismatchData.summary?.total_employees}</p>
                <p className="text-xs text-gray-500">Employees</p>
              </div>
              <div className={`rounded p-2 border ${mismatchData.summary?.total_mismatches > 0 ? 'bg-red-100 border-red-300' : 'bg-green-100 border-green-300'}`}>
                <p className="text-xl font-bold">{mismatchData.summary?.total_mismatches}</p>
                <p className="text-xs text-gray-600">Mismatches</p>
              </div>
            </div>

            {/* Office Admins List */}
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Office Admins ({mismatchData.office_admins?.length})</summary>
              <div className="mt-2 space-y-1 pl-4">
                {mismatchData.office_admins?.map((admin, idx) => (
                  <div key={idx} className={`p-2 rounded text-xs ${admin.office_exists ? 'bg-white' : 'bg-red-100'}`}>
                    <strong>{admin.name}</strong> ({admin.phone}) → Office: <code className="bg-gray-100 px-1 rounded">{admin.assigned_office_id}</code>
                    {!admin.office_exists && <Badge variant="destructive" className="ml-2">Invalid Office!</Badge>}
                  </div>
                ))}
              </div>
            </details>

            {/* Employees by Office */}
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Employees by Office</summary>
              <div className="mt-2 space-y-2 pl-4">
                {Object.entries(mismatchData.employees_by_office || {}).map(([officeId, info]) => (
                  <div key={officeId} className={`p-2 rounded ${info.office_exists ? 'bg-white' : 'bg-red-100'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <strong>Office ID:</strong> <code className="bg-gray-100 px-1 rounded">{officeId}</code>
                      {!info.office_exists && <Badge variant="destructive">Office doesn't exist!</Badge>}
                      <span className="text-gray-500">({info.employees?.length} employees)</span>
                    </div>
                    <ul className="text-xs pl-4 space-y-1">
                      {info.employees?.map(emp => (
                        <li key={emp.id} className="flex items-center gap-2">
                          <span>{emp.name} ({emp.employee_id})</span>
                          {!info.office_exists && offices.length > 0 && (
                            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => fixEmployeeOffice(emp.id, offices[0].id)}>
                              Move to {offices[0]?.name}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>

            {/* Mismatches */}
            {mismatchData.mismatches?.length > 0 && (
              <div className="bg-red-100 border border-red-300 rounded p-3">
                <h4 className="font-medium text-red-800 mb-2">⚠️ Issues Found:</h4>
                <ul className="space-y-1 text-sm">
                  {mismatchData.mismatches.map((m, idx) => (
                    <li key={idx} className="text-red-700">• {m.description}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggestions */}
            {mismatchData.suggestions?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3">
                <h4 className="font-medium text-yellow-800 mb-2">💡 Suggestions:</h4>
                <ul className="space-y-2 text-sm">
                  {mismatchData.suggestions.map((s, idx) => (
                    <li key={idx} className="text-yellow-700">
                      <strong>Issue:</strong> {s.issue}<br />
                      <strong>Fix:</strong> {s.suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Office Filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="h-5 w-5" />
            Filter by Office
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-xs">
            <Select value={selectedOffice} onValueChange={setSelectedOffice}>
              <SelectTrigger data-testid="office-filter">
                <SelectValue placeholder="Select office" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Offices</SelectItem>
                {offices.map((office) => (
                  <SelectItem key={office.id} value={office.id}>
                    {office.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Employees and Users */}
      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-5">
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Employees ({employees.length})
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="badges" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Badges ({employeesWithBadges.length})
          </TabsTrigger>
          <TabsTrigger value="orphans" className="flex items-center gap-2">
            <Unlink className="h-4 w-4" />
            Orphans {orphanData ? `(${orphanData.summary.total_orphans})` : ''}
          </TabsTrigger>
          <TabsTrigger value="delete-office" className="flex items-center gap-2 text-red-600" data-testid="delete-office-tab">
            <Trash2 className="h-4 w-4" />
            Delete Office
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Employee Records
                  </CardTitle>
                  <CardDescription>
                    Select employees to delete. This action cannot be undone.
                  </CardDescription>
                </div>
                {selectedEmployees.length > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={() => openDeleteDialog('employees')}
                    data-testid="delete-employees-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedEmployees.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or ID..."
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-10"
                    data-testid="employee-search"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSelectAllEmployees}
                >
                  {selectedEmployees.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No employees found</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-12 px-4 py-3 text-left">
                          <Checkbox 
                            checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0}
                            onCheckedChange={handleSelectAllEmployees}
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-medium">Employee</th>
                        <th className="px-4 py-3 text-left font-medium">Employee ID</th>
                        <th className="px-4 py-3 text-left font-medium">Department</th>
                        <th className="px-4 py-3 text-left font-medium">Office</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredEmployees.map((emp) => (
                        <tr 
                          key={emp.id} 
                          className={`hover:bg-gray-50 ${selectedEmployees.includes(emp.id) ? 'bg-red-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <Checkbox 
                              checked={selectedEmployees.includes(emp.id)}
                              onCheckedChange={() => handleSelectEmployee(emp.id)}
                              data-testid={`select-employee-${emp.id}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-xs font-medium text-blue-600">
                                  {emp.first_name?.[0]}{emp.last_name?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                                <p className="text-xs text-gray-500">{emp.contact?.email || emp.contact?.phone}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">{emp.employment?.employee_id || '-'}</td>
                          <td className="px-4 py-3">{emp.employment?.department || '-'}</td>
                          <td className="px-4 py-3">{emp.office_name || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge variant={emp.is_active ? 'default' : 'secondary'}>
                              {emp.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    User Accounts
                  </CardTitle>
                  <CardDescription>
                    Select users to delete. You cannot delete your own account.
                  </CardDescription>
                </div>
                {selectedUsers.length > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={() => openDeleteDialog('users')}
                    data-testid="delete-users-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedUsers.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or phone..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10"
                    data-testid="user-search"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSelectAllUsers}
                >
                  {selectedUsers.length === filteredUsers.filter(u => u.id !== user?.id).length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-12 px-4 py-3 text-left">
                          <Checkbox 
                            checked={selectedUsers.length === filteredUsers.filter(u => u.id !== user?.id).length && filteredUsers.length > 1}
                            onCheckedChange={handleSelectAllUsers}
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-medium">User</th>
                        <th className="px-4 py-3 text-left font-medium">Phone</th>
                        <th className="px-4 py-3 text-left font-medium">Role</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredUsers.map((u) => {
                        const isCurrentUser = u.id === user?.id;
                        return (
                          <tr 
                            key={u.id} 
                            className={`
                              ${isCurrentUser ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50'}
                              ${selectedUsers.includes(u.id) ? 'bg-red-50' : ''}
                            `}
                          >
                            <td className="px-4 py-3">
                              <Checkbox 
                                checked={selectedUsers.includes(u.id)}
                                onCheckedChange={() => handleSelectUser(u.id)}
                                disabled={isCurrentUser}
                                data-testid={`select-user-${u.id}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  getUserRole(u) === 'Super Admin' ? 'bg-red-100' : 
                                  getUserRole(u) === 'Office Admin' ? 'bg-blue-100' : 'bg-gray-100'
                                }`}>
                                  <span className={`text-xs font-medium ${
                                    getUserRole(u) === 'Super Admin' ? 'text-red-600' : 
                                    getUserRole(u) === 'Office Admin' ? 'text-blue-600' : 'text-gray-600'
                                  }`}>
                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium">
                                    {u.first_name} {u.last_name}
                                    {isCurrentUser && <span className="text-xs text-gray-500 ml-2">(You)</span>}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-xs">{u.phone}</td>
                            <td className="px-4 py-3">{getRoleBadge(u)}</td>
                            <td className="px-4 py-3">
                              <Badge variant={u.is_active ? 'default' : 'secondary'}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    Special Badges Management
                  </CardTitle>
                  <CardDescription>
                    View and remove special badges (Reviewer, Approver, Authorizer) from employees
                  </CardDescription>
                </div>
                {selectedBadgeEmployees.length > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowClearBadgesDialog(true)}
                    data-testid="clear-badges-btn"
                  >
                    <BadgeX className="h-4 w-4 mr-2" />
                    Clear Badges ({selectedBadgeEmployees.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employees with badges..."
                    value={badgeSearch}
                    onChange={(e) => setBadgeSearch(e.target.value)}
                    className="pl-9"
                    data-testid="badge-search"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : employeesWithBadges.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Award className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No employees have special badges assigned</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left">
                          <Checkbox 
                            checked={selectedBadgeEmployees.length === getFilteredBadgeEmployees().length && getFilteredBadgeEmployees().length > 0}
                            onCheckedChange={handleSelectAllBadgeEmployees}
                          />
                        </th>
                        <th className="px-4 py-3 text-left font-medium">Employee</th>
                        <th className="px-4 py-3 text-left font-medium">Department</th>
                        <th className="px-4 py-3 text-left font-medium">Special Badges</th>
                        <th className="px-4 py-3 text-left font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredBadgeEmployees().map((emp) => (
                        <tr key={emp.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Checkbox 
                              checked={selectedBadgeEmployees.includes(emp.id)}
                              onCheckedChange={() => handleSelectBadgeEmployee(emp.id)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                <User className="h-4 w-4 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                                <p className="text-xs text-gray-500">{emp.contact?.email || emp.employment?.employee_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {emp.employment?.department || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {emp.special_badges?.map((badge, idx) => (
                                <span 
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getBadgeColor(badge.badge_type)}`}
                                >
                                  {badge.badge_type}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {emp.special_badges?.map((badge, idx) => (
                                <Button
                                  key={idx}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-red-600 hover:text-red-800 hover:bg-red-50"
                                  onClick={() => clearSingleBadge(emp.id, badge.badge_type)}
                                  title={`Remove ${badge.badge_type} badge`}
                                >
                                  <BadgeX className="h-3 w-3 mr-1" />
                                  {badge.badge_type}
                                </Button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orphan Data Tab */}
        <TabsContent value="orphans">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Unlink className="h-5 w-5 text-orange-600" />
                    Orphan Data Scanner
                  </CardTitle>
                  <CardDescription>
                    Detect and manage orphaned records across the system
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={runDiagnosis} 
                    disabled={diagnosisLoading}
                    data-testid="diagnose-btn"
                  >
                    {diagnosisLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileWarning className="h-4 w-4 mr-2" />
                    )}
                    {diagnosisLoading ? 'Diagnosing...' : 'Deep Diagnosis'}
                  </Button>
                  <Button 
                    onClick={scanOrphanData} 
                    disabled={orphanLoading}
                    data-testid="scan-orphans-btn"
                  >
                    {orphanLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Scan className="h-4 w-4 mr-2" />
                    )}
                    {orphanLoading ? 'Scanning...' : 'Scan for Orphans'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {orphanError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{orphanError}</AlertDescription>
                </Alert>
              )}

              {/* Diagnosis Results */}
              {diagnosisData && (
                <div className="mb-6 space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-3">
                      <FileWarning className="h-5 w-5" />
                      Deep Diagnosis Results
                    </h3>
                    
                    {/* Offices Summary */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-purple-700 mb-2">Offices ({diagnosisData.offices?.length || 0})</h4>
                      <div className="flex flex-wrap gap-2">
                        {diagnosisData.offices?.map(office => (
                          <Badge 
                            key={office.id} 
                            variant={office.is_active ? "default" : "destructive"}
                            className="text-xs"
                          >
                            {office.name} ({diagnosisData.employees?.by_office?.[office.id]?.count || 0} employees)
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* Employees with Issues */}
                    {diagnosisData.employees?.issues?.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-red-700">
                            ⚠️ Employees with Issues ({diagnosisData.employees.issues.length})
                          </h4>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ALL ${diagnosisData.employees.issues.length} employees with issues? This cannot be undone.`)) {
                                diagnosisData.employees.issues.forEach(emp => {
                                  deleteOrphanRecord('employee', emp.id, emp.name);
                                });
                              }
                            }}
                            disabled={processingOrphan}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete All ({diagnosisData.employees.issues.length})
                          </Button>
                        </div>
                        <div className="bg-white rounded border max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">Employee</th>
                                <th className="px-3 py-2 text-left">Office</th>
                                <th className="px-3 py-2 text-left">Phone</th>
                                <th className="px-3 py-2 text-left">Issues</th>
                                <th className="px-3 py-2 text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {diagnosisData.employees.issues.map(emp => (
                                <tr key={emp.id} className="hover:bg-red-50">
                                  <td className="px-3 py-2">
                                    <div className="font-medium">{emp.name}</div>
                                    <div className="text-xs text-gray-500">{emp.employee_id}</div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div>{emp.office_name}</div>
                                    <div className="text-xs text-gray-500">{emp.office_id}</div>
                                  </td>
                                  <td className="px-3 py-2 text-xs">{emp.phone || 'N/A'}</td>
                                  <td className="px-3 py-2">
                                    {emp.issues.map((issue, idx) => (
                                      <Badge key={idx} variant="destructive" className="text-xs mr-1 mb-1">
                                        {issue}
                                      </Badge>
                                    ))}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-800 hover:bg-red-100"
                                      onClick={() => {
                                        if (window.confirm(`Delete employee "${emp.name}"?`)) {
                                          deleteOrphanRecord('employee', emp.id, emp.name);
                                        }
                                      }}
                                      disabled={processingOrphan === emp.id}
                                    >
                                      {processingOrphan === emp.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* All Employees by Office */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-purple-700 mb-2">
                        All Employees by Office (Total: {diagnosisData.employees?.total || 0})
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(diagnosisData.employees?.by_office || {}).map(([officeId, data]) => (
                          <details key={officeId} className="bg-white rounded border">
                            <summary className="px-3 py-2 cursor-pointer hover:bg-gray-50">
                              <span className="font-medium">{data.name}</span>
                              <Badge className="ml-2">{data.count} employees</Badge>
                            </summary>
                            <div className="px-3 py-2 border-t max-h-48 overflow-y-auto">
                              {data.employees?.length > 0 ? (
                                <ul className="text-sm space-y-1">
                                  {data.employees.map(emp => (
                                    <li key={emp.id} className={`flex items-center gap-2 ${emp.issues?.length > 0 ? 'text-red-600' : ''}`}>
                                      <span>{emp.employee_id}: {emp.name}</span>
                                      {emp.is_active === false && <Badge variant="destructive" className="text-xs">INACTIVE</Badge>}
                                      {emp.issues?.map((issue, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs text-red-600">{issue}</Badge>
                                      ))}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-500 text-sm">No employees in this office</p>
                              )}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                    
                    {/* Users without Employees */}
                    {diagnosisData.users?.without_employee?.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-orange-700">
                            ⚠️ Users without Employee Records ({diagnosisData.users.without_employee.length})
                          </h4>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ALL ${diagnosisData.users.without_employee.length} users without employee records? This cannot be undone.`)) {
                                diagnosisData.users.without_employee.forEach(usr => {
                                  deleteOrphanRecord('user', usr.id, usr.name);
                                });
                              }
                            }}
                            disabled={processingOrphan}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete All ({diagnosisData.users.without_employee.length})
                          </Button>
                        </div>
                        <div className="bg-white rounded border max-h-48 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left">User</th>
                                <th className="px-3 py-2 text-left">Phone</th>
                                <th className="px-3 py-2 text-left">Roles</th>
                                <th className="px-3 py-2 text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {diagnosisData.users.without_employee.map(user => (
                                <tr key={user.id} className="hover:bg-orange-50">
                                  <td className="px-3 py-2 font-medium">{user.name}</td>
                                  <td className="px-3 py-2 text-sm">{user.phone}</td>
                                  <td className="px-3 py-2">
                                    {user.roles?.map((r, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs mr-1">
                                        {r.role} @ {r.office_id || 'no office'}
                                      </Badge>
                                    ))}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-800 hover:bg-red-100"
                                      onClick={() => {
                                        if (window.confirm(`Delete user "${user.name}"?`)) {
                                          deleteOrphanRecord('user', user.id, user.name);
                                        }
                                      }}
                                      disabled={processingOrphan === user.id}
                                    >
                                      {processingOrphan === user.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="text-xs text-purple-600 mt-3">
                      Scanned at: {new Date(diagnosisData.summary?.scan_time).toLocaleString()} |
                      Total issues found: {diagnosisData.summary?.issues_found || 0}
                    </div>
                  </div>
                </div>
              )}

              {!orphanData && !diagnosisData && !orphanLoading && !diagnosisLoading && (
                <div className="text-center py-12 text-gray-500">
                  <Scan className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Click "Scan for Orphans" for quick scan or "Deep Diagnosis" for detailed analysis</p>
                </div>
              )}

              {orphanData && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {orphanData.summary.employees_without_users_count}
                      </p>
                      <p className="text-xs text-blue-800">Employees w/o Users</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {orphanData.summary.users_without_employees_count}
                      </p>
                      <p className="text-xs text-purple-800">Users w/o Employees</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">
                        {orphanData.summary.invalid_office_references_count}
                      </p>
                      <p className="text-xs text-orange-800">Invalid Office Refs</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">
                        {orphanData.summary.expenses_invalid_employee_count}
                      </p>
                      <p className="text-xs text-red-800">Orphan Expenses</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {orphanData.summary.letters_invalid_office_count}
                      </p>
                      <p className="text-xs text-yellow-800">Orphan Letters</p>
                    </div>
                  </div>

                  {/* Employees without Users */}
                  {orphanData.employees_without_users.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-blue-50 px-4 py-3 border-b flex items-center gap-2">
                        <Users className="h-4 w-4 text-blue-600" />
                        <span className="font-semibold text-blue-800">
                          Employees without User Accounts ({orphanData.employees_without_users.length})
                        </span>
                      </div>
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {orphanData.employees_without_users.map((emp) => (
                          <div key={emp.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{emp.name}</p>
                              <p className="text-sm text-gray-500">
                                {emp.employee_id} • {emp.department} • {emp.email}
                              </p>
                              <p className="text-xs text-orange-600">{emp.issue}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => deleteOrphanRecord('employee', emp.id, emp.name)}
                                disabled={processingOrphan === emp.id}
                              >
                                {processingOrphan === emp.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Users without Employees */}
                  {orphanData.users_without_employees.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-purple-50 px-4 py-3 border-b flex items-center gap-2">
                        <Shield className="h-4 w-4 text-purple-600" />
                        <span className="font-semibold text-purple-800">
                          Users without Employee Records ({orphanData.users_without_employees.length})
                        </span>
                      </div>
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {orphanData.users_without_employees.map((usr) => (
                          <div key={usr.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{usr.name}</p>
                              <p className="text-sm text-gray-500">
                                {usr.phone} • Roles: {usr.roles?.join(', ')}
                              </p>
                              <p className="text-xs text-orange-600">{usr.issue}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => deleteOrphanRecord('user', usr.id, usr.name)}
                                disabled={processingOrphan === usr.id}
                              >
                                {processingOrphan === usr.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Invalid Office References */}
                  {orphanData.invalid_office_references.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-orange-50 px-4 py-3 border-b flex items-center gap-2">
                        <Building className="h-4 w-4 text-orange-600" />
                        <span className="font-semibold text-orange-800">
                          Invalid Office References ({orphanData.invalid_office_references.length})
                        </span>
                      </div>
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {orphanData.invalid_office_references.map((ref, idx) => (
                          <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{ref.name}</p>
                              <p className="text-sm text-gray-500">
                                Collection: {ref.collection} • Invalid ID: {ref.invalid_office_id}
                              </p>
                              <p className="text-xs text-orange-600">{ref.issue}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fixOrphanRecord('clear-office', { 
                                  collection: ref.collection, 
                                  record_id: ref.record_id 
                                })}
                                disabled={processingOrphan === ref.record_id}
                              >
                                {processingOrphan === ref.record_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <Link2 className="h-4 w-4 mr-1" />
                                    Clear Ref
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Orphan Expenses */}
                  {orphanData.expenses_invalid_employee.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-red-50 px-4 py-3 border-b flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-red-600" />
                        <span className="font-semibold text-red-800">
                          Expenses with Invalid Employee ({orphanData.expenses_invalid_employee.length})
                        </span>
                      </div>
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {orphanData.expenses_invalid_employee.map((exp) => (
                          <div key={exp.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{exp.description}</p>
                              <p className="text-sm text-gray-500">
                                Amount: {exp.amount?.toLocaleString()} • Invalid Employee: {exp.invalid_employee_id?.substring(0, 15)}...
                              </p>
                              <p className="text-xs text-orange-600">{exp.issue}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => deleteOrphanRecord('expense', exp.id, exp.description)}
                                disabled={processingOrphan === exp.id}
                              >
                                {processingOrphan === exp.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Orphan Letters */}
                  {orphanData.letters_invalid_office.length > 0 && (
                    <div className="border rounded-lg">
                      <div className="bg-yellow-50 px-4 py-3 border-b flex items-center gap-2">
                        <FileText className="h-4 w-4 text-yellow-600" />
                        <span className="font-semibold text-yellow-800">
                          Letters with Invalid Office ({orphanData.letters_invalid_office.length})
                        </span>
                      </div>
                      <div className="divide-y max-h-64 overflow-y-auto">
                        {orphanData.letters_invalid_office.map((letter) => (
                          <div key={letter.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                            <div>
                              <p className="font-medium">{letter.reference_number}</p>
                              <p className="text-sm text-gray-500">
                                {letter.subject} • Invalid Office: {letter.invalid_office_id?.substring(0, 15)}...
                              </p>
                              <p className="text-xs text-orange-600">{letter.issue}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => deleteOrphanRecord('letter', letter.id, letter.reference_number)}
                                disabled={processingOrphan === letter.id}
                              >
                                {processingOrphan === letter.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No orphans found */}
                  {orphanData.summary.total_orphans === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                      <p className="text-lg font-medium text-green-600">No orphan data found!</p>
                      <p className="text-sm">All records are properly linked.</p>
                    </div>
                  )}

                  {/* Scan timestamp */}
                  <p className="text-xs text-gray-400 text-right">
                    Last scanned: {new Date(orphanData.summary.scan_time).toLocaleString()}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Delete Office Tab */}
        <TabsContent value="delete-office">
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <Trash2 className="h-5 w-5" />
                    Delete Office & All Data
                  </CardTitle>
                  <CardDescription>
                    Permanently delete an office and ALL its associated data. This action cannot be undone.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-6">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>WARNING:</strong> This will permanently delete the selected office and ALL associated data including:
                  employees, users, expenses, letters, leave requests, attendance, payroll, tasks, contracts, and more.
                </AlertDescription>
              </Alert>

              {/* Office Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-full max-w-md">
                    <Label htmlFor="office-select" className="mb-2 block">Select Office to Delete</Label>
                    <Select 
                      value={selectedDeleteOffice || ''} 
                      onValueChange={(value) => {
                        setSelectedDeleteOffice(value);
                        setOfficeDeletePreview(null);
                      }}
                    >
                      <SelectTrigger data-testid="delete-office-select">
                        <SelectValue placeholder="Choose an office..." />
                      </SelectTrigger>
                      <SelectContent>
                        {offices.map((office) => (
                          <SelectItem key={office.id} value={office.id}>
                            {office.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => previewOfficeDelete(selectedDeleteOffice)}
                    disabled={!selectedDeleteOffice || officeDeleteLoading}
                    className="mt-6"
                    data-testid="preview-delete-btn"
                  >
                    {officeDeleteLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Preview Deletion
                  </Button>
                </div>

                {/* Preview Results */}
                {officeDeletePreview && (
                  <div className="mt-6 space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                        <Building className="h-5 w-5" />
                        Deletion Preview: {officeDeletePreview.office.name}
                      </h3>
                      
                      {/* Warnings */}
                      {officeDeletePreview.warnings?.length > 0 && (
                        <div className="mb-4">
                          {officeDeletePreview.warnings.map((warning, idx) => (
                            <Alert key={idx} className="border-yellow-500 bg-yellow-50 mb-2">
                              <AlertTriangle className="h-4 w-4 text-yellow-600" />
                              <AlertDescription className="text-yellow-800">
                                {warning}
                              </AlertDescription>
                            </Alert>
                          ))}
                        </div>
                      )}
                      
                      {/* Data Summary Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.employees.count}
                          </p>
                          <p className="text-xs text-red-800">Employees</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.users.count}
                          </p>
                          <p className="text-xs text-red-800">Users</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.expenses.count}
                          </p>
                          <p className="text-xs text-red-800">Expenses</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.letters.count}
                          </p>
                          <p className="text-xs text-red-800">Letters</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.leave_requests.count}
                          </p>
                          <p className="text-xs text-red-800">Leave Requests</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.payroll_records.count}
                          </p>
                          <p className="text-xs text-red-800">Payroll Records</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.attendance_records.count}
                          </p>
                          <p className="text-xs text-red-800">Attendance</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-red-100">
                          <p className="text-2xl font-bold text-red-600">
                            {officeDeletePreview.related_data.tasks.count}
                          </p>
                          <p className="text-xs text-red-800">Tasks</p>
                        </div>
                      </div>
                      
                      {/* Employees List Preview */}
                      {officeDeletePreview.related_data.employees.items.length > 0 && (
                        <details className="mb-4">
                          <summary className="cursor-pointer text-sm font-medium text-red-700 hover:text-red-900">
                            View Employees to be Deleted ({officeDeletePreview.related_data.employees.count})
                          </summary>
                          <div className="mt-2 bg-white rounded border max-h-48 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">Name</th>
                                  <th className="px-3 py-2 text-left">Employee ID</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {officeDeletePreview.related_data.employees.items.map(emp => (
                                  <tr key={emp.id} className="hover:bg-red-50">
                                    <td className="px-3 py-2">{emp.name}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{emp.employee_id}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {officeDeletePreview.related_data.employees.count > 50 && (
                              <p className="text-xs text-gray-500 text-center py-2">
                                ... and {officeDeletePreview.related_data.employees.count - 50} more
                              </p>
                            )}
                          </div>
                        </details>
                      )}
                      
                      {/* Users List Preview */}
                      {officeDeletePreview.related_data.users.items.length > 0 && (
                        <details className="mb-4">
                          <summary className="cursor-pointer text-sm font-medium text-red-700 hover:text-red-900">
                            View Users to be Deleted/Updated ({officeDeletePreview.related_data.users.count})
                          </summary>
                          <div className="mt-2 bg-white rounded border max-h-48 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">Name</th>
                                  <th className="px-3 py-2 text-left">Phone</th>
                                  <th className="px-3 py-2 text-left">Roles</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {officeDeletePreview.related_data.users.items.map(usr => (
                                  <tr key={usr.id} className="hover:bg-red-50">
                                    <td className="px-3 py-2">{usr.name}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{usr.phone}</td>
                                    <td className="px-3 py-2">
                                      {usr.roles?.map((r, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs mr-1">{r}</Badge>
                                      ))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}
                      
                      {/* Total Summary */}
                      <div className="bg-red-100 rounded-lg p-3 text-center">
                        <p className="text-3xl font-bold text-red-700">
                          {officeDeletePreview.total_records}
                        </p>
                        <p className="text-sm text-red-800">Total Records to be Deleted</p>
                      </div>
                    </div>
                    
                    {/* Delete Button */}
                    <div className="flex justify-end">
                      <Button 
                        variant="destructive"
                        onClick={() => setShowDeleteOfficeDialog(true)}
                        disabled={!officeDeletePreview}
                        data-testid="delete-office-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Office & All Data
                      </Button>
                    </div>
                  </div>
                )}

                {/* No office selected state */}
                {!selectedDeleteOffice && !officeDeleteLoading && (
                  <div className="text-center py-12 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Select an office to preview what data will be deleted</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Clear Badges Confirmation Dialog */}
      <Dialog open={showClearBadgesDialog} onOpenChange={setShowClearBadgesDialog}>
        <DialogContent data-testid="clear-badges-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <BadgeX className="h-5 w-5" />
              Clear All Badges
            </DialogTitle>
            <DialogDescription>
              You are about to remove ALL special badges from {selectedBadgeEmployees.length} employee(s).
              This will affect their expense approval permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert className="border-orange-500 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                Removing badges will immediately revoke the employee's ability to review, approve, or authorize expenses.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="clearConfirm">Type <strong>CLEAR</strong> to confirm</Label>
              <Input
                id="clearConfirm"
                value={clearBadgesConfirmText}
                onChange={(e) => setClearBadgesConfirmText(e.target.value.toUpperCase())}
                placeholder="CLEAR"
                data-testid="clear-badges-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearBadgesDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleClearBadges}
              disabled={clearBadgesConfirmText !== 'CLEAR' || processing}
              data-testid="confirm-clear-badges-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <BadgeX className="h-4 w-4 mr-2" />
                  Clear All Badges
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete {deleteType === 'employees' ? selectedEmployees.length : selectedUsers.length} {deleteType}.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {deleteType === 'employees' 
                  ? 'Deleting employees will also remove their salary records, payroll data, and leave history.'
                  : 'Deleting users will revoke their login access permanently.'}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="confirm">Type <strong>DELETE</strong> to confirm</Label>
              <Input
                id="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || processing}
              data-testid="confirm-delete-btn"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete {deleteType === 'employees' ? 'Employees' : 'Users'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Office Confirmation Dialog */}
      <Dialog open={showDeleteOfficeDialog} onOpenChange={setShowDeleteOfficeDialog}>
        <DialogContent data-testid="delete-office-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Office Permanently
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete <strong>{officeDeletePreview?.office?.name}</strong> and 
              all {officeDeletePreview?.total_records} associated records.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>THIS ACTION CANNOT BE UNDONE!</strong><br />
                All employees, users, expenses, letters, payroll records, attendance, leave requests, 
                and other data associated with this office will be permanently deleted.
              </AlertDescription>
            </Alert>

            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">
                {officeDeletePreview?.total_records || 0} Records
              </p>
              <p className="text-sm text-red-700">will be permanently deleted</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deleteOfficeConfirm">
                Type <strong className="text-red-600">DELETE OFFICE</strong> to confirm
              </Label>
              <Input
                id="deleteOfficeConfirm"
                value={deleteOfficeConfirmText}
                onChange={(e) => setDeleteOfficeConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE OFFICE"
                className="border-red-300 focus:border-red-500"
                data-testid="delete-office-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteOfficeDialog(false);
                setDeleteOfficeConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteOffice}
              disabled={deleteOfficeConfirmText !== 'DELETE OFFICE' || processing}
              data-testid="confirm-delete-office-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Office Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDataManagement;
